import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { DashboardLayout } from "@/components/DashboardLayout";
import { studentNav } from "@/config/nav";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Loader2, ArrowLeft, Download, Clock, Check, X, Award, CheckCircle2, XCircle,
} from "lucide-react";

function Stat({ label, value, accent = "" }) {
  return (
    <div className="bg-card p-5">
      <div className="overline">{label}</div>
      <div className={`mt-1 text-2xl font-display font-bold ${accent}`}>{value}</div>
    </div>
  );
}

export default function ResultDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    api.get(`/attempts/${id}`).then(({ data }) => {
      if (data.status !== "published") { setPending(true); setAttempt(data); }
      else setAttempt(data);
    }).catch(() => navigate("/app/results"));
  }, [id, navigate]);

  const download = async () => {
    try {
      const res = await api.get(`/attempts/${id}/report`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${attempt.exam_title}-result.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  if (!attempt) return <DashboardLayout nav={studentNav} title="Result"><div className="py-20 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" /></div></DashboardLayout>;

  if (pending) {
    return (
      <DashboardLayout nav={studentNav} title="Result">
        <div className="border border-dashed border-border p-16 text-center max-w-lg mx-auto mt-10">
          <Clock className="h-10 w-10 mx-auto text-amber-500" />
          <h2 className="mt-4 font-display text-xl font-bold">Result not published yet</h2>
          <p className="mt-2 text-muted-foreground">This exam contains subjective questions awaiting evaluation. Your result will appear once the administrator publishes it.</p>
          <Button className="mt-6 rounded-sm" variant="outline" onClick={() => navigate("/app/results")}>Back to results</Button>
        </div>
      </DashboardLayout>
    );
  }

  const diff = attempt.difficulty_breakdown || {};

  return (
    <DashboardLayout nav={studentNav} title="Result">
      <button onClick={() => navigate("/app/results")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="h-4 w-4" /> Back to results
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">{attempt.exam_title}</h1>
          <p className="text-muted-foreground">{attempt.subject}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className={`px-4 py-2 font-semibold flex items-center gap-2 ${attempt.passed ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
            {attempt.passed ? <CheckCircle2 className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
            {attempt.passed ? "PASSED" : "FAILED"}
          </div>
          <Button onClick={download} className="rounded-sm" data-testid="download-report-btn"><Download className="h-4 w-4 mr-1.5" /> PDF Report</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border mb-8">
        <Stat label="Marks Obtained" value={`${attempt.total_score}/${attempt.total_marks}`} />
        <Stat label="Percentage" value={`${attempt.percentage}%`} accent="text-primary" />
        <Stat label="Objective" value={attempt.objective_score} />
        <Stat label="Subjective" value={attempt.subjective_score ?? 0} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-card border border-border p-6">
          <div className="overline mb-3">Answer Summary</div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="flex items-center gap-2 text-emerald-600"><Check className="h-4 w-4" /> Correct</span><span className="font-mono">{attempt.correct_count ?? 0}</span></div>
            <div className="flex justify-between"><span className="flex items-center gap-2 text-destructive"><X className="h-4 w-4" /> Incorrect</span><span className="font-mono">{attempt.incorrect_count ?? 0}</span></div>
            <div className="flex justify-between"><span className="flex items-center gap-2 text-muted-foreground">Skipped</span><span className="font-mono">{attempt.skipped_count ?? 0}</span></div>
          </div>
        </div>
        <div className="lg:col-span-2 bg-card border border-border p-6">
          <div className="overline mb-3">Difficulty-wise Performance</div>
          <div className="space-y-3">
            {["Easy", "Medium", "Hard"].filter((k) => diff[k]).map((k) => {
              const d = diff[k];
              const pct = d.total ? Math.round((100 * d.correct) / d.total) : 0;
              return (
                <div key={k}>
                  <div className="flex justify-between text-sm mb-1"><span>{k}</span><span className="font-mono">{d.correct}/{d.total} · {pct}%</span></div>
                  <div className="h-2 bg-secondary"><div className="h-full bg-primary" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
            {Object.keys(diff).length === 0 && <div className="text-sm text-muted-foreground">No objective questions.</div>}
          </div>
        </div>
      </div>

      {attempt.overall_feedback && (
        <div className="bg-accent border border-border p-6 mb-8">
          <div className="overline mb-2">Evaluator Feedback</div>
          <p className="text-sm">{attempt.overall_feedback}</p>
        </div>
      )}

      {/* question review */}
      <div className="bg-card border border-border p-6">
        <div className="overline mb-4">Question Review</div>
        <div className="space-y-4">
          {attempt.questions.map((q, i) => {
            const a = (attempt.answers || {})[q.id] || {};
            const ev = (attempt.evaluations || {})[q.id];
            const isObj = ["mcq", "msq"].includes(q.type);
            return (
              <div key={q.id} className="border border-border p-4">
                <div className="flex items-center gap-2 text-xs mb-2">
                  <span className="font-mono text-muted-foreground">Q{i + 1}</span>
                  <span className="px-1.5 py-0.5 bg-secondary uppercase">{q.type}</span>
                  <span className="font-mono text-muted-foreground ml-auto">{ev ? `${ev.marks}/${q.marks}` : `${q.marks} marks`}</span>
                </div>
                <p className="text-sm font-medium">{q.text}</p>
                {isObj ? (
                  <ul className="mt-2 space-y-1">
                    {q.options.map((o, idx) => {
                      const correct = q.correct_answers?.includes(idx);
                      const chosen = a.selected?.includes(idx);
                      return (
                        <li key={idx} className={`text-sm flex items-center gap-2 ${correct ? "text-emerald-700 font-medium" : chosen ? "text-destructive" : "text-muted-foreground"}`}>
                          {correct ? <Check className="h-3.5 w-3.5" /> : chosen ? <X className="h-3.5 w-3.5" /> : <span className="w-3.5" />}
                          {o}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div className="mt-2 bg-secondary/50 p-3 text-sm whitespace-pre-wrap">{a.text || <span className="italic text-muted-foreground">No answer</span>}</div>
                )}
                {ev?.feedback && <p className="mt-2 text-xs text-primary"><span className="font-medium">Feedback:</span> {ev.feedback}</p>}
                {q.explanation && <p className="mt-2 text-xs text-muted-foreground"><span className="font-medium">Explanation:</span> {q.explanation}</p>}
              </div>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
}
